/* entropica-charts.js
   Aromatic Matrix (radar) and Acid Complex (HPLC-style trace) for coffee product pages.
   Reads window.ENTROPICA_SENSORY = { aroma: [...], acid: [...] } (the custom.aroma_profile and
   custom.acid_complex metafields: [{ name, val 0-100, desc }]), set by sections/entropica-charts.liquid.

   Rules this file exists to enforce:
   - The scale starts at zero. Centre = absent, outer ring = 100. (Chart.js radar defaults to a floating
     minimum, which drew the weakest compound at the dead centre.)
   - No numbers shown to customers. Values are estimates, so the key shows Lead / Strong / Present /
     Background, never "68%".
   - Same charts on every viewport: radar for aroma (a set), trace for acids (an ordered series). On a
     narrow screen the radar swaps its printed names for numbered markers that match the key below.
   - The "estimated, not a lab test" line is static and readable.

   Source of truth: Entropica-Brain/99 Meta/Theme/entropica-charts/. Uploaded to the theme as
   assets/entropica-charts.js. The old Chart.js code in main-product-entropica.liquid is left in place and
   hidden by entropica-charts.css once this file mounts, so a load failure falls back to the old charts. */
(function () {
  'use strict';

  // The theme has "div:empty { display: none }", so the host must always hold a child to keep its width.
  var PROBE = '<i class="ec-probe" aria-hidden="true"></i>';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mctx = document.createElement('canvas').getContext('2d');

  function toArray(v) {
    if (typeof v === 'string') { try { v = JSON.parse(v); } catch (e) { return []; } }
    if (!Array.isArray(v)) return [];
    return v.map(function (d) {
      return {
        name: String((d && d.name) || ''),
        desc: String((d && d.desc) || ''),
        val: Math.max(0, Math.min(100, Number(d && d.val) || 0))
      };
    }).filter(function (d) { return d.name; });
  }
  function band(v) { return v >= 75 ? 'Lead' : v >= 60 ? 'Strong' : v >= 45 ? 'Present' : 'Background'; }
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; });
  }
  function f(n) { return String(Math.round(n * 10) / 10); }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function textW(txt, font, spacing) {
    mctx.font = font;
    return mctx.measureText(txt).width + (spacing || 0) * txt.length;
  }
  function ease(t) { return 1 - Math.pow(1 - t, 3); }
  function tween(ms, step, done) {
    if (reduceMotion) { step(1); if (done) done(); return; }
    var t0 = null;
    function frame(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / ms);
      step(ease(p));
      if (p < 1) requestAnimationFrame(frame); else if (done) done();
    }
    requestAnimationFrame(frame);
  }
  function leadIndex(items) { var l = 0; items.forEach(function (it, i) { if (it.val > items[l].val) l = i; }); return l; }
  function summary(items, what) {
    var order = items.slice().sort(function (a, b) { return b.val - a.val; });
    return 'Estimated ' + what + ', strongest first: ' + order.map(function (d) {
      return d.name + (d.desc ? ' (' + d.desc + ')' : '') + ', ' + band(d.val).toLowerCase();
    }).join('; ') + '.';
  }

  /* ---------- radar (aroma) ---------- */
  function radar(host, items, animate, opts) {
    opts = opts || {};
    var W = Math.floor(host.clientWidth);
    if (!W) return false;
    var n = items.length, small = W < 480, fs = opts.fontScale || 1;
    var nameSize = (small ? 10.5 : 12) * fs, descSize = (small ? 15 : 17) * fs, nameSp = (small ? 0.9 : 1.4) * fs;
    var nameFont = '500 ' + nameSize + 'px Montserrat, Arial, sans-serif';
    var descFont = 'italic 400 ' + descSize + 'px "Cormorant Garamond", Georgia, serif';
    var pad = 8, gap = 14, lh = descSize + 3;
    var sizes = items.map(function (it) {
      return { nw: textW(it.name.toUpperCase(), nameFont, nameSp), dw: textW(it.desc, descFont, 0) };
    });

    // 1) try printed labels around the radar; if the radius that fits is too small to read, use numbered markers
    var r = Math.min(small ? 96 : 132, W * 0.21 + (small ? 30 : 0)), minX = 0, maxX = 0, fit = false;
    for (; r >= 36; r -= 2) {
      minX = Infinity; maxX = -Infinity;
      items.forEach(function (it, i) {
        var sx = Math.sin(2 * Math.PI * i / n), w = Math.max(sizes[i].nw, sizes[i].dw), px = (r + gap) * sx, x0, x1;
        if (Math.abs(sx) < 0.2) { x0 = px - w / 2; x1 = px + w / 2; }
        else if (sx > 0) { x0 = px + 6; x1 = x0 + w; }
        else { x1 = px - 6; x0 = x1 - w; }
        minX = Math.min(minX, x0); maxX = Math.max(maxX, x1);
      });
      if (maxX - minX <= W - 2 * pad) { fit = true; break; }
    }
    var numbers = !opts.forceLabels && (!fit || r < (opts.minLabelRadius || 72));
    var cx, cy, H, labels = [];
    if (numbers) {
      r = Math.min(W * 0.3, 118);
      cx = W / 2; cy = r + 18 + 12 + 4; H = Math.ceil(2 * cy);
    } else {
      cx = pad + (W - 2 * pad - (maxX - minX)) / 2 - minX;
      labels = items.map(function (it, i) {
        var a = 2 * Math.PI * i / n, sx = Math.sin(a), cs = Math.cos(a);
        var px = cx + (r + gap) * sx, py = -(r + gap) * cs, anchor, dx, ny;
        if (Math.abs(sx) < 0.2) { anchor = 'middle'; dx = 0; } else if (sx > 0) { anchor = 'start'; dx = 6; } else { anchor = 'end'; dx = -6; }
        if (cs > 0.55) ny = py - 6 - lh;
        else if (cs < -0.55) ny = py + nameSize + 4;
        else ny = py - 1;
        return { x: px + dx, ny: ny, dy: ny + lh, anchor: anchor };
      });
      var minY = -r - 6, maxY = r + 6;
      labels.forEach(function (l) { minY = Math.min(minY, l.ny - nameSize); maxY = Math.max(maxY, l.dy + 6); });
      cy = pad - minY; H = Math.ceil(maxY - minY + 2 * pad);
    }

    function pt(i, scale) {
      var a = 2 * Math.PI * i / n;
      return [cx + r * scale * Math.sin(a), cy - r * scale * Math.cos(a)];
    }
    var lead = leadIndex(items);
    var s = '<svg class="ec-radar' + (numbers ? ' ec-radar--numbers' : '') + '" viewBox="0 0 ' + W + ' ' + H + '" width="' + W + '" height="' + H + '" role="img" aria-label="' + esc(summary(items, 'aroma compounds')) + '">';
    s += '<g class="ec-grid">';
    [0.25, 0.5, 0.75, 1].forEach(function (k) {
      s += '<polygon points="' + items.map(function (_, i) { return pt(i, k).map(f).join(','); }).join(' ') + '" class="' + (k === 1 ? 'ec-ring ec-ring--outer' : 'ec-ring') + '"/>';
    });
    items.forEach(function (_, i) { var p = pt(i, 1); s += '<line x1="' + f(cx) + '" y1="' + f(cy) + '" x2="' + f(p[0]) + '" y2="' + f(p[1]) + '" class="ec-spoke"/>'; });
    s += '</g>';
    s += '<polygon class="ec-poly" points="' + items.map(function () { return f(cx) + ',' + f(cy); }).join(' ') + '"/>';
    items.forEach(function (it, i) {
      s += '<g class="ec-dotg" data-i="' + i + '">' + (i === lead ? '<circle class="ec-halo" r="9" cx="' + f(cx) + '" cy="' + f(cy) + '"/>' : '') +
           '<circle class="ec-dot' + (i === lead ? ' ec-dot--lead' : '') + '" r="' + (i === lead ? 4.6 : 3.4) + '" cx="' + f(cx) + '" cy="' + f(cy) + '"/></g>';
    });
    items.forEach(function (it, i) {
      var p = pt(i, 1), aria = esc(it.name + ', ' + it.desc + ', ' + band(it.val).toLowerCase());
      s += '<g class="ec-axis" data-i="' + i + '" tabindex="0" role="button" aria-label="' + aria + '">';
      if (numbers) {
        var b = pt(i, (r + 20) / r);
        s += '<circle class="ec-hit" cx="' + f(b[0]) + '" cy="' + f(b[1]) + '" r="18"/>' +
             '<circle class="ec-badge" cx="' + f(b[0]) + '" cy="' + f(b[1]) + '" r="10.5"/>' +
             '<text class="ec-badge-n" x="' + f(b[0]) + '" y="' + f(b[1]) + '" font-size="10">' + (i + 1) + '</text>';
      } else {
        var l = labels[i];
        s += '<circle class="ec-hit" cx="' + f(p[0]) + '" cy="' + f(p[1]) + '" r="16"/>' +
             '<text class="ec-name" x="' + f(l.x) + '" y="' + f(cy + l.ny) + '" text-anchor="' + l.anchor + '" font-size="' + nameSize + '" letter-spacing="' + nameSp + '">' + esc(it.name.toUpperCase()) + '</text>' +
             (it.desc ? '<text class="ec-desc" x="' + f(l.x) + '" y="' + f(cy + l.dy) + '" text-anchor="' + l.anchor + '" font-size="' + descSize + '">' + esc(it.desc) + '</text>' : '');
      }
      s += '</g>';
    });
    s += '</svg>';
    host.insertAdjacentHTML('beforeend', s);
    host.classList.toggle('ec-mode-numbers', numbers);

    var svg = host.querySelector('.ec-radar'), poly = svg.querySelector('.ec-poly');
    var dots = svg.querySelectorAll('.ec-dotg');
    function setShape(k) {
      poly.setAttribute('points', items.map(function (it, i) { return pt(i, (it.val / 100) * k).map(f).join(','); }).join(' '));
      dots.forEach(function (g, i) {
        var p = pt(i, (items[i].val / 100) * k);
        g.querySelectorAll('circle').forEach(function (c) { c.setAttribute('cx', f(p[0])); c.setAttribute('cy', f(p[1])); });
      });
    }
    if (animate) tween(900, setShape); else setShape(1);
    return true;
  }

  /* ---------- trace (acids): the site's original chromatogram look, noise and all ---------- */
  var traceSeq = 0;
  function trace(host, items, animate) {
    if (!Math.floor(host.clientWidth)) return false;
    // same geometry, noise and peak shape as the original mobile chromatogram (760 x 380 viewBox)
    var W = 760, H = 380, mL = 26, mR = 26, mT = 54, mB = 30;
    var n = items.length, baseY = H - mB, topY = mT, plotH = baseY - topY, usableW = W - mL - mR;
    var peaks = items.map(function (it, i) {
      return {
        cx: mL + usableW * (i + 0.5) / n,
        amp: it.val > 0 ? Math.max(8, it.val / 100 * plotH) : 0,
        sigma: Math.min(40, Math.max(4, usableW / (n * 20)))
      };
    });
    function noise(x) { return Math.sin(x * 0.11) * 1.5 + Math.sin(x * 0.34 + 1.7) * 0.9 + Math.sin(x * 0.78 + 4.1) * 0.55; }
    function yAt(x) {
      var y = baseY + noise(x);
      peaks.forEach(function (p) { y -= p.amp * Math.exp(-((x - p.cx) * (x - p.cx)) / (2 * p.sigma * p.sigma)); });
      return y;
    }
    var d = '', x;
    for (x = mL; x <= W - mR; x += 1) d += (x === mL ? 'M' : 'L') + x.toFixed(1) + ' ' + yAt(x).toFixed(1) + ' ';
    var area = d + 'L' + (W - mR) + ' ' + baseY + ' L' + mL + ' ' + baseY + ' Z';
    var gid = 'ecFill' + (++traceSeq), lead = leadIndex(items), slot = usableW / n;
    var s = '<svg class="ec-trace" viewBox="0 0 ' + W + ' ' + H + '" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" role="img" aria-label="' + esc(summary(items, 'acids') + ' Simulated HPLC trace, peak height is relative prominence.') + '">';
    s += '<defs><linearGradient id="' + gid + '" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="rgba(255,255,255,0.18)"/><stop offset="100%" stop-color="rgba(255,255,255,0)"/></linearGradient></defs>';
    s += '<line class="ec-base" x1="' + mL + '" y1="' + baseY + '" x2="' + (W - mR) + '" y2="' + baseY + '"/>';
    s += '<path class="ec-area" d="' + area + '" fill="url(#' + gid + ')"/><path class="ec-line" d="' + d + '"/>';
    items.forEach(function (it, i) {
      var p = peaks[i], apexY = yAt(p.cx), labelY = Math.max(topY - 6, apexY - 14);
      s += '<g class="ec-peak ec-pnum' + (i === lead ? ' is-lead' : '') + '" data-i="' + i + '" tabindex="0" role="button" aria-label="' + esc(it.name + ', ' + it.desc + ', ' + band(it.val).toLowerCase()) + '">' +
           '<rect class="ec-hit" x="' + f(p.cx - slot / 2) + '" y="' + (topY - 24) + '" width="' + f(slot) + '" height="' + (plotH + 50) + '"/>' +
           '<line x1="' + f(p.cx) + '" y1="' + f(apexY - 6) + '" x2="' + f(p.cx) + '" y2="' + f(labelY + 4) + '"/>' +
           '<text x="' + f(p.cx) + '" y="' + f(labelY) + '">' + pad2(i + 1) + '</text></g>';
    });
    s += '</svg><div class="ec-axislabel">RETENTION TIME →</div>';
    host.insertAdjacentHTML('beforeend', s);
    host.classList.add('ec-mode-numbers');

    var svg = host.querySelector('.ec-trace');
    var line = svg.querySelector('.ec-line'), fill = svg.querySelector('.ec-area'), marks = svg.querySelectorAll('.ec-pnum');
    if (animate && !reduceMotion && line.getTotalLength) {
      var len = line.getTotalLength();
      line.style.strokeDasharray = len; line.style.strokeDashoffset = len; line.style.transition = 'none';
      void line.getBoundingClientRect();
      line.style.transition = 'stroke-dashoffset 1.7s cubic-bezier(0.22, 1, 0.36, 1)';
      requestAnimationFrame(function () { line.style.strokeDashoffset = '0'; });
      setTimeout(function () { fill.classList.add('is-lit'); }, 1400);
      marks.forEach(function (m, i) { setTimeout(function () { m.classList.add('is-lit'); }, 1700 + i * 120); });
    } else {
      fill.classList.add('is-lit'); marks.forEach(function (m) { m.classList.add('is-lit'); });
    }
    return true;
  }

  /* ---------- key (same order as the chart: radar clockwise from the top, trace left to right) ---------- */
  function key(items) {
    var lead = leadIndex(items);
    return '<ol class="ec-key" aria-label="Relative prominence of each compound">' + items.map(function (it, i) {
      return '<li class="ec-key__row' + (i === lead ? ' is-lead' : '') + '" data-i="' + i + '" tabindex="0">' +
        '<span class="ec-key__num" aria-hidden="true">' + pad2(i + 1) + '</span>' +
        '<div class="ec-key__text"><span class="ec-key__name">' + esc(it.name) + '</span>' + (it.desc ? '<span class="ec-key__desc">' + esc(it.desc) + '</span>' : '') + '</div>' +
        '<span class="ec-key__band">' + band(it.val) + '</span>' +
        '<span class="ec-key__bar" aria-hidden="true"><i data-v="' + it.val + '"></i></span></li>';
    }).join('') + '</ol>';
  }

  /* ---------- mount ---------- */
  var NOTES = {
    aroma: 'Estimated from the process and the roaster’s notes, not a lab test. The closer a point sits to the edge, the more prominent that compound is in the cup.',
    acid: 'Estimated the same way and drawn as a simulated HPLC trace. Taller peaks are more prominent in the cup.'
  };

  function render(host, kind, items, animate) {
    host.innerHTML = PROBE;
    host.classList.toggle('ec-wide', host.clientWidth >= 560);
    var ok = (kind === 'aroma' && items.length >= 3) ? radar(host, items, animate) : (kind === 'acid' && items.length >= 2) ? trace(host, items, animate) : true;
    if (!ok) return false;
    host.insertAdjacentHTML('beforeend', '<p class="ec-note">' + NOTES[kind] + '</p>' + key(items));
    var bars = host.querySelectorAll('.ec-key__bar i');
    function lightBars() { bars.forEach(function (b) { b.style.width = b.getAttribute('data-v') + '%'; }); }
    if (animate && !reduceMotion) setTimeout(lightBars, 120); else lightBars();

    // highlight: hover (mouse) or focus shows it briefly, a click or tap pins it, a click elsewhere clears it
    var hover = null, pinned = null;
    function apply() {
      var i = hover !== null ? hover : pinned;
      host.classList.toggle('has-hot', i !== null);
      host.querySelectorAll('[data-i]').forEach(function (el) { el.classList.toggle('is-hot', i !== null && el.getAttribute('data-i') === String(i)); });
    }
    host.__ecClear = function () { hover = null; pinned = null; apply(); };
    host.querySelectorAll('.ec-axis, .ec-peak, .ec-key__row').forEach(function (el) {
      var i = el.getAttribute('data-i');
      el.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { hover = i; apply(); } });
      el.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { hover = null; apply(); } });
      el.addEventListener('focus', function () { hover = i; apply(); });
      el.addEventListener('blur', function () { hover = null; apply(); });
      el.addEventListener('click', function (e) { e.stopPropagation(); pinned = (pinned === i) ? null : i; apply(); });
    });
    return true;
  }

  function mount() {
    var D = window.ENTROPICA_SENSORY || {};
    var sets = { aroma: toArray(D.aroma), acid: toArray(D.acid) };
    var mounted = [];
    document.querySelectorAll('.terminal-accordion').forEach(function (acc) {
      var btn = acc.querySelector('.terminal-accordion-btn'), inner = acc.querySelector('.terminal-accordion-inner');
      if (!btn || !inner || acc.getAttribute('data-ec')) return;
      var t = btn.textContent;
      var kind = /aromatic matrix/i.test(t) ? 'aroma' : /acid complex/i.test(t) ? 'acid' : null;
      if (!kind || sets[kind].length < 2) return;
      var host = document.createElement('div');
      host.className = 'ec-sensory ec-sensory--' + kind;
      host.innerHTML = PROBE;
      inner.insertBefore(host, inner.firstChild);
      acc.setAttribute('data-ec', kind);
      var lastW = 0;
      function draw(animate, tries) {
        if (!acc.classList.contains('is-open')) { host.innerHTML = PROBE; lastW = 0; return; }
        if (!host.clientWidth && (tries || 0) < 6) { setTimeout(function () { draw(animate, (tries || 0) + 1); }, 120); return; }
        lastW = Math.floor(host.clientWidth);
        render(host, kind, sets[kind], animate);
      }
      btn.addEventListener('click', function () { setTimeout(function () { draw(true, 0); }, 80); });
      mounted.push({ acc: acc, host: host, draw: draw, width: function () { return lastW; } });
      if (acc.classList.contains('is-open')) draw(false, 0);
    });
    if (!mounted.length) return 0;
    document.addEventListener('click', function () {
      document.querySelectorAll('.ec-sensory').forEach(function (h) { if (h.__ecClear) h.__ecClear(); });
    });
    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () {
        mounted.forEach(function (m) {
          if (m.acc.classList.contains('is-open') && Math.abs(Math.floor(m.host.clientWidth) - m.width()) > 4) m.draw(false, 0);
        });
      }, 160);
    });
    return mounted.length;
  }

  if (window.__EC_DEBUG) window.__ec = { radar: radar, trace: trace, render: render, key: key, toArray: toArray, band: band, NOTES: NOTES, PROBE: PROBE };

  function start() { mount(); }
  var fontsReady = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { fontsReady.then(start, start); });
  else fontsReady.then(start, start);
})();
